import { z } from "zod"

import { MaturityDateTypeGql, PayPatternGql, PaymentRuleEventGql } from "@/graphql/codegen/graphql"
import { isValueInRange } from "@/utils/forms"

const payDaysValidation = {
  [PayPatternGql.Weekly]: { min: 1, max: 7, message: "For Weekly pay pattern, pay days must be between 1 and 7" },
  [PayPatternGql.BiWeekly]: { min: 1, max: 14, message: "For Biweekly pay pattern, pay days must be between 1 and 14" },
  [PayPatternGql.Monthly]: { min: 1, max: 31, message: "For Monthly pay pattern, pay days must be between 1 and 31" },
}

const offsetDaysValidation = {
  [PayPatternGql.Weekly]: {
    min: -52,
    max: 52,
    message: "For Weekly pay pattern, offset days must be between -52 and 52",
  },
  [PayPatternGql.BiWeekly]: {
    min: -26,
    max: 26,
    message: "For Biweekly pay pattern, offset days must be between -26 and 26",
  },
  [PayPatternGql.Monthly]: {
    min: -12,
    max: 12,
    message: "For Monthly pay pattern, offset days must be between -12 and 12",
  },
}

export const PaymentTermsSchema = z.object({
  name: z
    .string()
    .min(1, { message: "Payment term name is mandatory" })
    .max(20, { message: "Payment term name should be up to 20 characters." }),
  options: z.array(
    z.object({
      parts: z.array(
        z
          .object({
            paymentRule: z.object({
              daysAfter: z
                .string()
                .min(1, { message: "Days is mandatory" })
                .refine((value) => isValueInRange(value, 0, 364), {
                  message: "Days must be a valid number less than 365 and greater than or equal to 0",
                }),
              event: z.nativeEnum(PaymentRuleEventGql).default(PaymentRuleEventGql.PlacingOrder),
              percentage: z
                .string()
                .min(1, { message: "Pay percentage is mandatory" })
                .refine((value) => isValueInRange(value, 0.01, 100), {
                  message: "Pay percentage must be a valid number between 0.01% and 100%",
                }),
            }),
            includeSalesDiscount: z.boolean().default(false),
            discount: z.object({
              percentage: z
                .string()
                .optional()
                .refine((value) => !value || isValueInRange(value, 0.01, 100), {
                  message: "Discount percentage must be a valid number between 0.01% and 100%",
                }),
              applicableIfWithinDays: z
                .string()
                .optional()
                .refine((value) => !value || isValueInRange(value, 0, 364), {
                  message: "Discount days must be a valid number less than 365 and greater than or equal to 0",
                }),
            }),
          })
          .refine(
            (data) =>
              !(data.includeSalesDiscount && !(data.discount?.percentage && data.discount?.applicableIfWithinDays)),
            {
              message:
                "Both discount percentage and applicableIfWithinDays are required if Include Sales Discount is selected.",
              path: ["discount"],
            }
          )
      ),
    })
  ),
  payRunSchedule: z
    .object({
      isEnabled: z.boolean().default(false),
      payPattern: z.nativeEnum(PayPatternGql).default(PayPatternGql.Weekly),
      startDate: z.string().optional(),
      payDays: z.string().optional(),
      invoiceMaturity: z.object({
        intervalBetweenPayRuns: z.nativeEnum(MaturityDateTypeGql).default(MaturityDateTypeGql.PayInterval),
        offsetInDays: z.string().optional(),
      }),
    })
    .superRefine((data, ctx) => {
      if (!data.isEnabled) return

      const validatePayDays = (pattern: PayPatternGql) => {
        const { min, max, message } = payDaysValidation[pattern]
        const payDays = data.payDays?.split(",") ?? []

        if (!payDays.length || payDays.some((v) => !isValueInRange(v, min, max))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payDays"],
            message,
          })
        }
      }

      const validateOffsetDays = (pattern: PayPatternGql) => {
        const offsetInDays = data.invoiceMaturity?.offsetInDays
        if (!offsetInDays) return

        const { min, max, message } = offsetDaysValidation[pattern]
        const formatedOffsetInDays = Number(offsetInDays)
        if (formatedOffsetInDays < min || formatedOffsetInDays > max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["invoiceMaturity", "offsetInDays"],
            message,
          })
        }
      }

      if (!data.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: "Start (reference date) is required",
        })
      }

      // Validate based on pay pattern selected
      if (data.payPattern) {
        validatePayDays(data.payPattern as PayPatternGql)
        validateOffsetDays(data.payPattern as PayPatternGql)
      }
    }),
})

export type PaymentTermsSchemaFormValues = z.infer<typeof PaymentTermsSchema>
